import React from 'react';
import './ShipmentsProjection.css';

function CertaintyRing({ pct }) {
  const radius = 22;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (pct / 100) * circ;
  const color = pct >= 70 ? '#46BB89' : pct >= 40 ? '#F6AD55' : '#FC8181';
  return (
    <div className="sp-ring-wrap">
      <svg width={56} height={56} viewBox="0 0 56 56">
        <circle cx={28} cy={28} r={radius} stroke="rgba(255,255,255,0.08)" strokeWidth={5} fill="none" />
        <circle
          cx={28} cy={28} r={radius}
          stroke={color} strokeWidth={5} fill="none"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 28 28)"
          style={{ transition: 'stroke-dashoffset 0.6s ease' }}
        />
      </svg>
      <span className="sp-ring-pct" style={{ color }}>{pct}%</span>
    </div>
  );
}

function AccuracyBadge({ pct }) {
  const color = pct >= 70 ? '#46BB89' : pct >= 45 ? '#F6AD55' : '#FC8181';
  const bg    = pct >= 70 ? 'rgba(70,187,137,0.1)' : pct >= 45 ? 'rgba(246,173,85,0.1)' : 'rgba(252,129,129,0.1)';
  const border = pct >= 70 ? 'rgba(70,187,137,0.3)' : pct >= 45 ? 'rgba(246,173,85,0.3)' : 'rgba(252,129,129,0.3)';
  return (
    <span className="sp-accuracy-badge" style={{ color, background: bg, borderColor: border }}>
      {pct}%
    </span>
  );
}

export default function ShipmentsProjection({ meta, loading, error, projectedCount, granularity }) {
  if (!loading && !meta && !error) return null;

  const unitLabel = granularity === 'week' ? 'weeks' : granularity === 'day' ? 'days' : 'months';
  const certainty = meta?.certaintyPercent ?? null;
  const certaintyLabel = certainty == null ? null : certainty >= 70 ? 'High certainty' : certainty >= 40 ? 'Moderate certainty' : 'Low certainty';
  const productEntries = meta?.productProjections
    ? Object.entries(meta.productProjections)
        .map(([pn, d]) => ({ pn, total: d.projectedTotal, note: d.trendNote, accuracy: d.accuracyPercent }))
        .sort((a, b) => b.total - a.total)
    : [];
  const hasBacktest        = meta?.backtestAccuracy != null;
  const validityWarnings   = meta?.validityWarnings?.filter(Boolean)   || [];
  const improvementActions = meta?.improvementActions?.filter(Boolean) || [];

  return (
    <div className="sp-root">
      <div className="sp-header">
        <span className="sp-header-badge">
          <span className="sp-header-star">◈</span>
          Forecast projection
        </span>
        {!loading && projectedCount > 0 && (
          <span className="sp-header-sub">{projectedCount} {unitLabel} projected</span>
        )}
      </div>

      {loading && (
        <div className="sp-thinking">
          <div className="sp-thinking-orb">
            <span className="sp-thinking-star">◈</span>
          </div>
          <div className="sp-thinking-copy">
            <span className="sp-thinking-title">Building projection model…</span>
            <span className="sp-thinking-sub">Analysing seasonality, growth rate and product trends</span>
          </div>
        </div>
      )}

      {!loading && error && (
        <div className="sp-error">{error}</div>
      )}

      {!loading && !error && meta && (
        <div className="sp-body">
          {/* Certainty + backtest + headline metrics */}
          <div className="sp-top-row">
            {certainty != null && (
              <div className="sp-certainty-card">
                <CertaintyRing pct={certainty} />
                <div className="sp-certainty-copy">
                  <span className="sp-certainty-label">{certaintyLabel}</span>
                  {hasBacktest ? (
                    <span className="sp-certainty-sub">
                      Backtest: <strong style={{ color: meta.backtestAccuracy >= 70 ? '#46BB89' : meta.backtestAccuracy >= 45 ? '#F6AD55' : '#FC8181' }}>{meta.backtestAccuracy}% accurate</strong> on held-out data
                    </span>
                  ) : (
                    <span className="sp-certainty-sub">Shorter horizons yield higher certainty</span>
                  )}
                </div>
              </div>
            )}
            <div className="sp-meta-chips">
              {meta.growthRate && (
                <div className="sp-chip">
                  <span className="sp-chip-key">Growth rate</span>
                  <span className="sp-chip-val">{meta.growthRate}</span>
                </div>
              )}
              {meta.seasonalPattern && (
                <div className="sp-chip">
                  <span className="sp-chip-key">Seasonality</span>
                  <span className="sp-chip-val">{meta.seasonalPattern}</span>
                </div>
              )}
            </div>
          </div>

          {/* AI validity warnings */}
          {validityWarnings.length > 0 && (
            <div className="sp-validity-warnings">
              <div className="sp-validity-head">⚠ Model validity notes</div>
              <ul className="sp-validity-list">
                {validityWarnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}

          {/* Methodology */}
          {meta.methodology && (
            <p className="sp-methodology">{meta.methodology}</p>
          )}

          {/* Per-product forecast */}
          {productEntries.length > 0 && (
            <div className="sp-products">
              <div className="sp-products-head">Projected production by product</div>
              <div className="sp-products-subhead">Accuracy score reflects historical consistency of each product's share</div>
              <div className="sp-product-list">
                {productEntries.map(({ pn, total, note, accuracy }) => (
                  <div key={pn} className="sp-product-row">
                    <div className="sp-product-left">
                      <div className="sp-product-name-row">
                        <span className="sp-product-name">{pn}</span>
                        {accuracy != null && <AccuracyBadge pct={accuracy} />}
                      </div>
                      {note && <span className="sp-product-note">{note}</span>}
                    </div>
                    <span className="sp-product-total">{(total || 0).toLocaleString()} <span>units</span></span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Improvement actions */}
          {improvementActions.length > 0 && (
            <div className="sp-improve">
              <div className="sp-improve-head">How to improve forecast accuracy</div>
              <ul className="sp-improve-list">
                {improvementActions.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </div>
          )}

          {/* Assumptions + risks */}
          <div className="sp-sections">
            {meta.keyAssumptions?.length > 0 && (
              <div className="sp-section">
                <div className="sp-section-head">Key assumptions</div>
                <ul className="sp-list">
                  {meta.keyAssumptions.map((a, i) => <li key={i}>{a}</li>)}
                </ul>
              </div>
            )}
            {meta.risks?.length > 0 && (
              <div className="sp-section sp-section--risk">
                <div className="sp-section-head">Risk factors</div>
                <ul className="sp-list">
                  {meta.risks.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
